'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Image from 'next/image';

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Init animate-on-scroll observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    );
    const els = sectionRef.current?.querySelectorAll('.animate-on-scroll') ?? [];
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      className="min-h-screen relative flex items-center justify-center overflow-hidden pt-24">
      
      {/* Background image with teal overlay */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1587201457627-0bd9ba0ce76f"
          alt="Plage tropicale en Guadeloupe avec eau turquoise et ciel bleu"
          fill
          className="object-cover object-center"
          priority />
        
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/80 to-background/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/40 to-transparent" />
      </div>

      {/* Left decorative image */}
      <div
        className="absolute left-8 top-1/2 -translate-y-1/2 w-44 h-64 hidden xl:block animate-on-scroll anim-in delay-400"
        style={{ animationPlayState: 'paused' }}>
        
        <div className="w-full h-full overflow-hidden border border-primary/20 bg-primary/5 p-1.5">
          <AppImage
            src="https://images.unsplash.com/photo-1581974206939-b42731ea9dc9"
            alt="Villa de location saisonnière avec piscine en Guadeloupe"
            fill
            className="object-cover opacity-85 hover:opacity-100 transition-opacity duration-700" />
          
        </div>
        <span className="absolute -bottom-7 left-0 text-[8px] uppercase tracking-ultra text-foreground-subtle">
          Anse des Rochers
        </span>
      </div>

      {/* Right decorative image */}
      <div
        className="absolute right-8 top-1/2 -translate-y-1/2 w-44 h-64 hidden xl:block animate-on-scroll anim-in delay-500"
        style={{ animationPlayState: 'paused' }}>
        
        <div className="w-full h-full overflow-hidden border border-accent/25 bg-accent/5 p-1.5">
          <AppImage
            src="https://images.unsplash.com/photo-1709984408100-98556678835e"
            alt="Mer turquoise et sable blanc des Antilles au coucher du soleil"
            fill
            className="object-cover opacity-85 hover:opacity-100 transition-opacity duration-700" />
          
        </div>
        <span className="absolute -bottom-7 right-0 text-[8px] uppercase tracking-ultra text-foreground-subtle text-right">
          Guadeloupe, 2026
        </span>
      </div>

      {/* Center content */}
      <div className="relative z-10 container mx-auto px-6 text-center max-w-5xl">
        {/* Partnership badge */}
        <div
          className="animate-on-scroll anim-in inline-flex items-center gap-3 mb-8"
          style={{ animationPlayState: 'paused' }}>
          
          <Image
            src="/assets/images/cropped-cropped-logo-petit-1772038143329.jpeg"
            alt="Full-Loc logo"
            width={80}
            height={32}
            className="object-contain"
          />
          <span className="w-8 h-px bg-accent" />
          <span className="text-[10px] uppercase tracking-ultra text-accent font-medium">
            Partenariat officiel · Anse des Rochers
          </span>
          <span className="w-8 h-px bg-accent" />
        </div>

        {/* Main headline */}
        <div
          className="animate-on-scroll anim-in delay-100 mb-6"
          style={{ animationPlayState: 'paused' }}>
          
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[0.92] tracking-tight text-foreground">
            Anse des Rochers
            <br />
            <span className="italic text-teal-gradient">accompagne</span>
            <br />
            <span className="font-light">ses propriétaires</span>
          </h1>
          <p className="font-serif-display text-3xl md:text-5xl lg:text-6xl italic text-accent mt-2 leading-tight">
            vers l'accueil digital de demain.
          </p>
        </div>

        {/* Subtitle */}
        <p
          className="animate-on-scroll anim-in delay-200 max-w-2xl mx-auto text-[13px] md:text-sm text-foreground-muted leading-relaxed tracking-wide mb-12 px-4"
          style={{ animationPlayState: 'paused' }}>
          
          Améliorez l'expérience voyageur, valorisez votre annonce et modernisez votre accueil
          grâce au livret numérique recommandé — <strong className="text-foreground font-medium">entièrement gratuit</strong> pour les propriétaires Anse des Rochers.
        </p>

        {/* CTA Buttons */}
        <div
          className="animate-on-scroll anim-in delay-300 flex flex-col sm:flex-row items-center justify-center gap-4"
          style={{ animationPlayState: 'paused' }}>
          
          <a
            href="https://livretvacancesguadeloupe.fr/connexion"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center gap-3 bg-primary hover:bg-primary-dark text-white px-10 py-4 text-[11px] uppercase tracking-wide-xl font-medium transition-all duration-400 overflow-hidden">
            
            <span className="relative z-10">Activer mon livret gratuit</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="relative z-10 group-hover:translate-x-1 transition-transform duration-300">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
          <a
            href="#pourquoi"
            onClick={(e) => handleScroll(e, '#pourquoi')}
            className="group inline-flex items-center gap-3 border border-foreground/20 hover:border-primary/50 bg-white/50 hover:bg-white/80 backdrop-blur-sm text-foreground px-10 py-4 text-[11px] uppercase tracking-wide-xl font-medium transition-all duration-400">
            
            <span>Découvrir comment ça fonctionne</span>
          </a>
        </div>

        {/* Trust signal */}
        <div
          className="animate-on-scroll anim-in delay-400 mt-14 flex items-center justify-center gap-6 flex-wrap"
          style={{ animationPlayState: 'paused' }}>
          
          {['Gratuit', 'Sans engagement', 'Activation simple'].map((tag) =>
          <div key={tag} className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span className="text-[11px] text-foreground-muted tracking-wide">{tag}</span>
            </div>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-on-scroll anim-in delay-600" style={{ animationPlayState: 'paused' }}>
        <div className="w-px h-16 bg-gradient-to-b from-primary to-transparent" />
      </div>
    </section>);

}